import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Languages, BookA } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AutoTextarea } from "@/components/AutoTextarea";
import { PageHeader } from "@/components/CategoryBadge";
import { api, errMsg } from "@/lib/api";
import { useLanguages, useInvalidate } from "@/lib/queries";

function NewLanguageDialog({ uid, onOpenChange, onCreated }) {
  const invalidate = useInvalidate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/universes/${uid}/languages`, { name, description });
      invalidate(["languages", uid], ["entities", uid]);
      toast.success(`${data.name} created`, { description: "Also added to the Lore Bible" });
      onCreated(data.id);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-language-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">New language</DialogTitle>
            <DialogDescription>Describe it freely. You can sort the description into grammar sections on the next page.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-5">
            <div className="space-y-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Old Varnic" data-testid="new-language-name" /></div>
            <div className="space-y-2">
              <Label>How it works</Label>
              <AutoTextarea value={description} onChange={(e) => setDescription(e.target.value)} maxVh={50} className="min-h-[200px] leading-relaxed"
                placeholder="Who speaks it, its sounds, sentence order, verbs, how it's written vs spoken…" data-testid="new-language-description" />
            </div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy || !name.trim()} data-testid="new-language-save">{busy ? "Creating…" : "Create language"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function LanguagesPage() {
  const { universe, canEdit, base } = useOutletContext();
  const navigate = useNavigate();
  const { data: langs = [] } = useLanguages(universe.id);
  const [creating, setCreating] = useState(false);
  return (
    <div className="px-4 sm:px-8 lg:px-12 py-10">
      <PageHeader eyebrow={`${langs.length} languages`} title="Languages">
        {canEdit && <Button onClick={() => setCreating(true)} data-testid="new-language-button"><Plus className="h-4 w-4 mr-1" />New language</Button>}
      </PageHeader>
      {langs.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#2D3442] p-14 text-center" data-testid="languages-empty">
          <Languages className="mx-auto h-8 w-8 text-[#14B8A6]" />
          <p className="mt-4 font-display text-3xl">No tongues recorded yet.</p>
          <p className="mt-2 text-sm text-[#A8A090]">Describe a language here, or process a note about one in the AI scribe.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="languages-grid">
          {langs.map((l, i) => (
            <motion.button key={l.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.04 }}
              onClick={() => navigate(`${base}/languages/${l.id}`)} data-testid={`language-card-${l.id}`}
              className="group text-left rounded-md border border-[#2D3442] bg-[#13171F] p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#14B8A6]/60"
              style={{ borderLeft: "3px solid #14B8A6" }}>
              <div className="flex items-center justify-between text-[#14B8A6]"><Languages className="h-4 w-4" /><span className="flex items-center gap-1 text-xs font-mono-lf"><BookA className="h-3.5 w-3.5" />{l.word_count} words</span></div>
              <div className="font-display text-2xl mt-3 text-[#F3EBDD] group-hover:text-[#5EEAD4] transition-colors">{l.name}</div>
              <p className="mt-1.5 text-sm text-[#A8A090] line-clamp-2">{l.description || "No description yet."}</p>
            </motion.button>
          ))}
        </div>
      )}
      {creating && <NewLanguageDialog uid={universe.id} onOpenChange={setCreating} onCreated={(id) => navigate(`${base}/languages/${id}`)} />}
    </div>
  );
}
